import { memo, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ALL_SERVICES } from 'shared-modules/constants/core';
import { useCurrentCartConfirmation } from 'shared-modules/services/hooks/cartLogic';
import { useAccountInfo } from 'shared-modules/hooks/useAccountInfo';
import { checkCartStrategiesType } from 'shared-modules/utils';
import { makeNotificationErrorParams } from 'shared-modules/utils/service';
import { useSummaryInfoByServiceId } from 'shared-modules/services/hooks';
import { BUILDER_REQUIRED_MARGIN_VALIDATION_MESSAGE } from 'shared-modules/constants/builder';
import { FX, ETF, CFD, SERVICE_SHORT_NAME_DEF } from 'shared-modules/constants';
import { saveCreatingStrategiesInfo } from 'shared-modules/redux/actions/builderActions';
import {
  executeCartItemsRequest,
  openConfirmationModal,
  openStrategiesSuccessModal,
  closeConfirmationModal,
  sendNotificationError,
  setCartDataServiceId,
  openErrorInfoModal,
  getApGroupRequest,
} from '../../../../../redux/actions';
import { Button } from '../../../../../components';
import styles from './footer.module.scss';

const BUTTON_SIZE = 160;

export const Footer = memo(({ errorMessages, onSave, disabledAllSave, disabledAllRun }) => {
  const dispatch = useDispatch();
  const defaultSelectionTermId = useSelector((state) => state.constants.defaultSelectionTermId);
  const storedCartItems = useSelector((state) => state.cart.currentData[defaultSelectionTermId]);
  const isCartItemsExecuting = useSelector((state) => state.cart.currentDataExecuteIsLoading);
  const cartItems = useMemo(() => storedCartItems ?? [], [storedCartItems]);
  const serviceId = useSelector((state) => state.auth.serviceId);
  const accountInfo = useAccountInfo();
  const { isDisabled, isLoading, marginRequiredFX, marginRequiredETF, marginRequiredCFD } =
    useCurrentCartConfirmation(errorMessages);

  useEffect(() => {
    dispatch(getApGroupRequest({ serviceId: 'fx' }));
    dispatch(getApGroupRequest({ serviceId: 'etf' }));
    dispatch(getApGroupRequest({ serviceId: 'cfd' }));
  }, [dispatch]);

  const hasServiceItem = useCallback(
    (service) => cartItems.some(({ strategyDetail }) => strategyDetail.serviceId === service),
    [cartItems],
  );

  const orderableMarginFX = useSummaryInfoByServiceId(FX)?.orderableMargin;
  const orderableMarginETF = useSummaryInfoByServiceId(ETF)?.orderableMargin;
  const orderableMarginCFD = useSummaryInfoByServiceId(CFD)?.orderableMargin;

  const enoughOrderableMarginFX = !marginRequiredFX || orderableMarginFX >= marginRequiredFX;
  const enoughOrderableMarginCFD = !marginRequiredCFD || orderableMarginCFD >= marginRequiredCFD;
  const enoughOrderableMarginETF = !marginRequiredETF || orderableMarginETF >= marginRequiredETF;
  const enoughOrderableMargin = enoughOrderableMarginFX && enoughOrderableMarginCFD && enoughOrderableMarginETF;

  const handleExecuteOrder = useCallback(() => {
    if (isCartItemsExecuting) {
      return false;
    }

    if (!enoughOrderableMargin) {
      const notEnoughAssetName = [
        !enoughOrderableMarginFX && SERVICE_SHORT_NAME_DEF[FX],
        !enoughOrderableMarginETF && SERVICE_SHORT_NAME_DEF[ETF],
        !enoughOrderableMarginCFD && SERVICE_SHORT_NAME_DEF[CFD],
      ].filter(Boolean);
      dispatch(
        openErrorInfoModal({
          title: 'エラー',
          message: `${notEnoughAssetName.join(',')}の${BUILDER_REQUIRED_MARGIN_VALIDATION_MESSAGE}`,
          buttonText: '閉じる',
        }),
      );
      return false;
    }

    const notAvailableService = ALL_SERVICES.find(
      (service) => hasServiceItem(service) && accountInfo[service].isNotAvailable,
    );
    if (notAvailableService) {
      const errorParams = makeNotificationErrorParams({ accountInfo, serviceId: notAvailableService });
      dispatch(closeConfirmationModal());
      dispatch(sendNotificationError(errorParams));
      return false;
    }

    const cartStrategiesServiceId = checkCartStrategiesType(cartItems, serviceId);

    const callback = (resultArray) => {
      dispatch(openStrategiesSuccessModal({ resultArray }));
      dispatch(setCartDataServiceId({ serviceId: cartStrategiesServiceId }));
    };
    const strategiesInfo = storedCartItems.map(({ strategyId, strategyDetail }) => ({
      strategyId,
      serviceId: strategyDetail?.serviceId,
    }));
    dispatch(saveCreatingStrategiesInfo({ strategies: strategiesInfo }));
    return dispatch(executeCartItemsRequest({ callback }));
  }, [
    dispatch,
    hasServiceItem,
    accountInfo,
    cartItems,
    isCartItemsExecuting,
    serviceId,
    enoughOrderableMargin,
    enoughOrderableMarginFX,
    enoughOrderableMarginETF,
    enoughOrderableMarginCFD,
    storedCartItems,
  ]);

  const handleOpenConfirmationModal = useCallback(() => {
    if (isDisabled || disabledAllRun || isCartItemsExecuting) {
      return;
    }
    dispatch(
      openConfirmationModal({
        title: '確認画面',
        callback: handleExecuteOrder,
        bodyText: `注文確定を行います。よろしいでしょうか。
          ※注文本数が多い場合は、注文完了までに時間がかかる場合があります。`,
        buttonNextText: '自動売買を開始',
        isButtonGreen: true,
      }),
    );
  }, [handleExecuteOrder, dispatch, isDisabled, disabledAllRun, isCartItemsExecuting]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.buttonArea}>
        <Button width={BUTTON_SIZE} loading={isLoading} disabled={disabledAllSave} onClick={onSave} secondary>
          一括保存
        </Button>
        <Button
          className={styles.greenButton}
          width={BUTTON_SIZE}
          loading={isLoading}
          disabled={isDisabled || disabledAllRun || isCartItemsExecuting}
          onClick={handleOpenConfirmationModal}
          secondary
        >
          確認画面へ進む
        </Button>
      </div>
    </div>
  );
});

Footer.propTypes = {
  onSave: PropTypes.func.isRequired,
  errorMessages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  disabledAllSave: PropTypes.bool.isRequired,
  disabledAllRun: PropTypes.bool,
};

Footer.defaultProps = {
  disabledAllRun: false,
};
